'use client';
// Reviews v2 PREVIEW: first `initial` cards, then "הצג עוד המלצות" reveals `step` more (bughunter 29/09/26 — the old LIMIT 50
// left reviews 51+ unreachable). Order comes from the server (text-first).
import { Children, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import ReviewSlider, { ReviewSlide } from './ReviewSlider';
import { useTagOrder, tagLabel } from './tagContext';

// label: always 'המלצות' (reviews V5, 30/09/26) — (every pro page reaches all its reviews since 30/09/26)
// M3 (Founder 02/10/26): the cards ride THE review slider (components/ReviewSlider); "הצג עוד" stays — as the slider's last slide.
const DESK_FIRST = 8;
const DESK_STEP = 12;
export default function ReviewsV2More({ children, initial = 50, step = 50, label = 'המלצות', proId, ids }: { children: ReactNode; initial?: number; step?: number; label?: string; proId: number; ids?: number[] }) {
  const base = Children.toArray(children);
  // tag-search (14/10/26): reviews whose TAGS match the visitor's search come first (ids = review ids aligned with the children). Neutral order without a search context.
  const { order, reset } = useTagOrder(proId);
  const { all, matched } = useMemo(() => {
    if (!order || !ids || ids.length !== base.length) return { all: base, matched: 0 };
    const pos = new Map(ids.map((id, i) => [id, i])); const firstIdx = order.reviews.map((id) => pos.get(id)).filter((i): i is number => i != null);
    const f = new Set(firstIdx);
    return { all: [...firstIdx.map((i) => base[i]), ...base.filter((_, i) => !f.has(i))], matched: firstIdx.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, ids, children]);
  const [shown, setShown] = useState(initial);
  // desktop ≥1024 (direction A, 16/10/26): the same cards as a 2-column grid (CSS .rv-grid-lg), the first DESK_FIRST then ONE "הצג עוד"; phones <768 (21/10/26) the same batch as a vertical list; tablet never reads this
  const [deskShown, setDeskShown] = useState(DESK_FIRST); // batch-based (16/10/26): +DESK_STEP per click, count on the button = what remains
  const left = all.length - Math.max(shown, deskShown);
  const deskLeft = Math.max(0, all.length - deskShown);
  const renderN = Math.max(shown, deskShown);
  // bughunter 02/10 (gate-1.8.1 round): after "הצג עוד" the slider landed on the LAST card (measured live: visible 99–100 of 101).
  // The jump now runs AFTER React committed the new cards (effect on `shown`), on the scroller itself: the first NEW card, snap kept.
  const scroller = useRef<HTMLDivElement | null>(null);
  const landOn = useRef<number | null>(null);
  useEffect(() => {
    const first = landOn.current; const el = scroller.current; landOn.current = null;
    if (first == null || !el) return;
    const card = el.children[first] as HTMLElement | undefined; if (!card) return;
    const snap = el.style.scrollSnapType; el.style.scrollSnapType = 'none';
    const sr = el.getBoundingClientRect(), cr = card.getBoundingClientRect();
    // RTL: the card's right edge meets the scroller's right edge (minus the bleed padding)
    el.scrollLeft += cr.right - (sr.right - parseFloat(getComputedStyle(el).paddingRight || '0'));
    requestAnimationFrame(() => { el.style.scrollSnapType = snap; });
  }, [shown]);
  // tablet 768–1023 (16/10/26): the slider is as tall as its TALLEST card (e.g. a 325px review among 142px ones) → ~150px of empty space under the
  // visible cards. Fit its height to the cards currently in view. Phones (<768) and desktop (≥1024 = grid) are never touched.
  useEffect(() => {
    const el = scroller.current; if (!el) return;
    const mq = window.matchMedia('(min-width:768px) and (max-width:1023px)');
    let raf = 0;
    const fit = () => {
      raf = 0;
      if (!mq.matches) { if (el.style.height) el.style.height = ''; return; }
      el.style.height = '';
      const sr = el.getBoundingClientRect(); let max = 0;
      for (const k of Array.from(el.children) as HTMLElement[]) {
        const r = k.getBoundingClientRect(); const vis = Math.min(r.right, sr.right) - Math.max(r.left, sr.left);
        if (vis > r.width * 0.3) max = Math.max(max, r.height);
      }
      if (max > 0 && max < el.getBoundingClientRect().height) el.style.height = `${Math.ceil(max + (parseFloat(getComputedStyle(el).paddingBottom) || 0))}px`;
    };
    const sched = () => { if (!raf) raf = requestAnimationFrame(fit); };
    fit(); el.addEventListener('scroll', sched, { passive: true }); window.addEventListener('resize', sched); el.addEventListener('click', () => setTimeout(sched, 60));
    const t = setTimeout(sched, 800);
    return () => { el.removeEventListener('scroll', sched); window.removeEventListener('resize', sched); clearTimeout(t); if (raf) cancelAnimationFrame(raf); el.style.height = ''; };
  }, [shown, order]);
  return (
    <>
    {order && matched > 0 && (
      <div data-tag-chip="reviews" className="flex items-center gap-2 mb-2 text-xs" style={{ color: '#1d4ed8' }}>
        <span className="px-2.5 py-1 rounded-full" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>מציג קודם המלצות על: {tagLabel(order)} ({matched})</span>
        <button type="button" onClick={reset} className="underline" style={{ color: '#64748b', minHeight: 32 }}>סדר רגיל</button>
      </div>
    )}
    <style>{`@media (min-width:1024px),(max-width:767px){.rv-slider.rv-grid-lg>.rv-slide:nth-child(n+${deskShown + 1}){display:none}}`}</style>
    <ReviewSlider proId={proId} bleed scrollRef={scroller} className="rv-grid-lg">
      {all.slice(0, renderN).map((c, i) => <ReviewSlide key={i}>{c}</ReviewSlide>)}
      {all.length - renderN > 0 && (
        <div className="flex-shrink-0 flex items-center justify-center rv-inmore" style={{ alignSelf: 'stretch', paddingInline: 8, scrollSnapAlign: 'end' /* patrol 02/10: a mandatory-snap slider must be able to stop here */ }}>
          <button type="button" onClick={() => {
              // patrol 02/10: after loading, land on the FIRST new review — otherwise snap re-lands on this (moved) button and the
              // new reviews are skipped unseen (the jump itself: the effect above, after the commit)
              landOn.current = shown; setShown(s => s + step);
            }}
            className="text-sm font-semibold px-5 py-2.5 rounded-full"
            style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', minHeight: 44, whiteSpace: 'nowrap' }}>
            הצג עוד {label} (<bdi dir="ltr">{left}</bdi>)
          </button>
        </div>
      )}
    </ReviewSlider>
    {deskLeft > 0 && (
      <div className="rv-dmore">
        <button type="button" onClick={() => setDeskShown(n => Math.min(all.length, n + DESK_STEP))}>הצג עוד {label} (<bdi dir="ltr">{deskLeft}</bdi>)</button>
      </div>
    )}
    </>
  );
}
